import { IStaticPageItem } from '@ui';
import { isEmpty } from 'lodash';
import React, { memo, useCallback } from 'react';
import { KColors, KContainer, KDims, KGrid, KImage, KLabel } from 'uikit';

import './index.css';

interface IProps {
  data: Array<IStaticPageItem>;
  keepSpaceForEmptyElement?: boolean;
  withDesktopVersion?: boolean;
  withSpace?: boolean;
  withParent?: boolean;
}

const TemplateLayout = ({
  data,
  // keepSpaceForEmptyElement,
  withDesktopVersion,
  withSpace = true,
  withParent = true
}: IProps) => {
  const isMobile = KDims.isMobile();

  const renderBlocks = useCallback(
    (blocks: IStaticPageItem['blocks']) => {
      return blocks.map((b, bIdx) => {
        const renderDescription = () => {
          if (isEmpty(b.description)) {
            return null;
          }

          return (
            <>
              {b.description.map((d, dIdx) => {
                if (d.type === 'string') {
                  return (
                    <KGrid.Item
                      xs={12}
                      key={`des-${dIdx}`}
                      sx={{ display: 'flex' }}
                    >
                      <KLabel.Paragraph
                        dangerouslySetInnerHTML={{
                          __html: d.value[0]
                        }}
                        style={{ fontSize: 16 }}
                        marginV="0.5rem"
                      />
                    </KGrid.Item>
                  );
                }

                if (d.order) {
                  return (
                    <ol key={`des-${dIdx}`} start={d.start ?? 1}>
                      {d.value.map((v, vIdx) => {
                        return (
                          <li
                            key={`desVal-${vIdx}`}
                            dangerouslySetInnerHTML={{
                              __html: v
                            }}
                          />
                        );
                      })}
                    </ol>
                  );
                }

                return (
                  <ul key={`des-${dIdx}`}>
                    {d.value.map((v, vIdx) => {
                      return (
                        <li
                          key={`desVal-${vIdx}`}
                          dangerouslySetInnerHTML={{
                            __html: v
                          }}
                        />
                      );
                    })}
                  </ul>
                );
              })}
            </>
          );
        };

        const renderImage = () => {
          if (!b.image) {
            return null;
          }

          return (
            <KGrid.Item
              xs={isMobile ? 12 : undefined}
              alignItems="center"
              justifyContent="center"
              sx={{
                display: 'flex'
              }}
            >
              <KImage.Base
                source={`/images/static/${b.image}`}
                width={'100%' as any}
                paddingB={b.imageFullWidth ? '0.75rem' : undefined}
                style={b.imageStyle}
              />
            </KGrid.Item>
          );
        };

        return (
          <KGrid.Item
            xs={12}
            key={`block-${bIdx}`}
            sx={{
              display: 'flex',
              paddingBottom: 0,
              backgroundColor: b.background || KColors.white,
              marginLeft: b.imageFullWidth ? '-20px' : undefined,
              marginRight: b.imageFullWidth ? '-20px' : undefined
            }}
            width={b.imageFullWidth ? 'calc(100% + 40px)' : undefined}
          >
            <KContainer.View
              marginH={-12 as any}
              width={isMobile ? undefined : '100%'}
            >
              <KGrid.Container sx={{ display: 'flex' }}>
                {(b.title || b.subTitle) && (
                  <KGrid.Item
                    xs={12}
                    sx={{ display: 'flex', flexDirection: 'column' }}
                  >
                    {!!b.title && (
                      <KLabel.Paragraph typo="H2">{b.title}</KLabel.Paragraph>
                    )}

                    {!!b.subTitle && (
                      <KLabel.Paragraph
                        typo="H3"
                        marginT={!!b.title ? '1.25rem' : 0}
                      >
                        {b.subTitle}
                      </KLabel.Paragraph>
                    )}
                  </KGrid.Item>
                )}

                {b.row ? (
                  <KContainer.View row alignItems="flex-start" gap={24}>
                    <KContainer.View dp="flex" flex={2}>
                      {renderDescription()}
                    </KContainer.View>

                    <KContainer.View dp="flex" flex={1}>
                      {renderImage()}
                    </KContainer.View>
                  </KContainer.View>
                ) : (
                  <>
                    {renderDescription()}
                    {renderImage()}
                  </>
                )}
              </KGrid.Container>
            </KContainer.View>
          </KGrid.Item>
        );
      });
    },
    [isMobile]
  );

  return (
    <KGrid.Container sx={{ display: 'flex' }}>
      {data.map((i, idx) => {
        if (!withParent) {
          return renderBlocks(i.blocks);
        }

        return (
          <KGrid.Item
            xs={withDesktopVersion ? (isMobile ? 12 : 6) : 12}
            key={`section-${idx}`}
            sx={{ display: 'flex', paddingLeft: 0, paddingRight: 0 }}
          >
            <KContainer.View
              // marginH={-12 as any}
              marginV={-12 as any}
              width={isMobile ? undefined : '100%'}
              paddingT={withSpace ? '1rem' : 0}
              paddingH="1.25rem"
              background={i.background || KColors.white}
              // paddingB={
              //   withSpace &&
              //   (keepSpaceForEmptyElement ||
              //     idx === data.length - 1 ||
              //     (withDesktopVersion && !isMobile))
              //     ? '1rem'
              //     : !!i.background
              //     ? '0.25rem'
              //     : 0
              // }
            >
              <KGrid.Container sx={{ display: 'flex' }}>
                {!!i.title && (
                  <KGrid.Item
                    xs={12}
                    sx={{
                      display: 'flex',
                      paddingBottom:
                        !i.subTitle && !i.description ? 0 : undefined
                    }}
                  >
                    <KLabel.Paragraph typo="H1">{i.title}</KLabel.Paragraph>
                  </KGrid.Item>
                )}

                {!!i.subTitle && (
                  <KGrid.Item
                    xs={12}
                    sx={{
                      display: 'flex',
                      paddingBottom: !i.description ? 0 : undefined
                    }}
                  >
                    <KLabel.Paragraph typo="H2">{i.subTitle}</KLabel.Paragraph>
                  </KGrid.Item>
                )}

                {!!i.description && (
                  <KGrid.Item xs={12} sx={{ display: 'flex' }}>
                    <KLabel.Paragraph
                      dangerouslySetInnerHTML={{
                        __html: i.description
                      }}
                      style={{ fontSize: 16 }}
                    />
                  </KGrid.Item>
                )}

                {renderBlocks(i.blocks)}
              </KGrid.Container>
            </KContainer.View>
          </KGrid.Item>
        );
      })}
    </KGrid.Container>
  );
};

export default memo(TemplateLayout);
